// ClientEditProfile.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Snackbar, Alert, Stack, Typography, Grid } from "@mui/material";
import config from "../../config";
import axios from "axios";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";

import SectionImages from "./sections/images";
import SectionMainInfo from "./sections/main";
import SectionPricing from "./sections/pricing";
import SectionReviews from "./sections/reviews";
import SectionLocation from "./sections/location";
import SectionIncludes from "./sections/includes";

export const OTHER_VALUE = "other";

export const emptyForm = {
  name: "",
  logo: "",
  coverImg: "",
  logoPosition: { x: 50, y: 50, scale: 1 },

  category: "",
  parentCategory: "",
  occasions: [],

  location: { town: "", coords: ["", ""], address: "", postal_code: "" },
  covers: [],

  contact_info: { phone: "", email: "", website: "", facebook: "", instagram: "", tiktok: "", youtube: "" },
  info: "",
  contactTimes: "All day",
  notifyBefore: "",

  price: { hour: "", day: "", event: "" },
  googleReviews: { rating: "", link: "" },
  includes: [],
  coversEverywhere: false,
};

export function ensureArray(val) {
  return Array.isArray(val) ? val : [];
}

function parseRealAddress(realAddress) {
  if (!realAddress) return { address: "", postal_code: "" };
  const lastComma = realAddress.lastIndexOf(", ");
  if (lastComma === -1) return { address: realAddress, postal_code: "" };
  return {
    address: realAddress.slice(0, lastComma),
    postal_code: realAddress.slice(lastComma + 2),
  };
}

export function normalizePrice(val) {
  if (val === null || val === undefined) return "";
  if (val === "") return "";
  const n = Number(val);
  return Number.isFinite(n) ? n : "";
}

export default function ClientEditProfile() {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({ s1: false, s2: false, s3: false, s4: false, s5: false });
  const [form, setForm] = useState(emptyForm);

  const [occasionOptions, setOccasionOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });
  const notify = (type, msg) => setToast({ open: true, type, msg });

  const initialS1 = useRef(null);
  const initialS2 = useRef(null);
  const initialS3 = useRef(null);
  const initialS4 = useRef(null);
  const initialS5 = useRef(null);
  const initialS6 = useRef(null);

  const token = useMemo(() => localStorage.getItem("admin_jwt"), []);

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));
  const setNested = (rootKey, key, value) =>
    setForm((p) => ({ ...p, [rootKey]: { ...(p[rootKey] || {}), [key]: value } }));
  const setLocation = (key, value) =>
    setForm((p) => ({ ...p, location: { ...(p.location || {}), [key]: value } }));

  const citySelectOptions = useMemo(() => {
    const lang = i18n.language;
    return (cityOptions || []).map((c) => ({
      value: c.value,
      label: lang === "gr" ? (c.nameGr || c.nameEn) : c.nameEn,
      coords: c.coords,
      nameEn: c.nameEn,
      nameGr: c.nameGr,
    }));
  }, [cityOptions, i18n.language]);

  const isDirty = (ref, current) => {
    if (!ref.current) return false;
    return JSON.stringify(ref.current) !== JSON.stringify(current);
  };

  const updatePartial = async (payload, savingKey) => {
    try {
      setSaving((p) => ({ ...p, [savingKey]: true }));

      await axios.put(`${config.BACKEND_BASE_URL}/api/client/me/editable`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setToast({ open: true, type: "success", msg: t("info.updateSuccess") });
      return true;
    } catch (err) {
      console.error("Update failed:", err);
      setToast({ open: true, type: "error", msg: t("info.updateFailed") });
      return false;
    } finally {
      setSaving((p) => ({ ...p, [savingKey]: false }));
    }
  };

  const updateLocationSection = async (payload) => {
    try {
      setSaving((p) => ({ ...p, s4: true }));

      await axios.put(`${config.BACKEND_BASE_URL}/api/admin/info/update/location`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      notify("success", t("info.locationSuccess"));
      return true;
    } catch (err) {
      console.error("Update location failed:", err);
      notify("error", t("info.locationFailed"));
      return false;
    } finally {
      setSaving((p) => ({ ...p, s4: false }));
    }
  };

  const updatePricingSection = async (payload) => {
    try {
      setSaving((p) => ({ ...p, s3: true }));

      await axios.put(`${config.BACKEND_BASE_URL}/api/admin/info/update/pricing`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      notify("success", t("info.pricingSuccess"));
      return true;
    } catch (err) {
      console.error("Update pricing failed:", err);
      notify("error", t("info.pricingFailed"));
      return false;
    } finally {
      setSaving((p) => ({ ...p, s3: false }));
    }
  };

  const updateReviewsSection = async (payload) => {
    try {
      setSaving((p) => ({ ...p, s5: true }));

      await axios.put(`${config.BACKEND_BASE_URL}/api/admin/info/update/reviews`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      notify("success", t("info.reviewsSuccess"));
      return true;
    } catch (err) {
      console.error("Update reviews failed:", err);
      notify("error", t("info.reviewsFailed"));
      return false;
    } finally {
      setSaving((p) => ({ ...p, s5: false }));
    }
  };

  const uploadLogoWithAdjustments = async ({ file, logoPosition }) => {
    try {
      setSaving((p) => ({ ...p, s1: true }));

      const fd = new FormData();
      fd.append("logo", file);
      fd.append("logoPosition", JSON.stringify(logoPosition));

      const res = await axios.post(`${config.BACKEND_BASE_URL}/api/admin/images/client/logo`, fd, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });

      const nextLogo = res.data?.logo || "";
      const nextLogoPosition = res.data?.logoPosition || logoPosition;

      setForm((prev) => ({
        ...prev,
        logo: nextLogo,
        logoPosition: {
          x: Number(nextLogoPosition?.x ?? 50),
          y: Number(nextLogoPosition?.y ?? 50),
          scale: Number(nextLogoPosition?.scale ?? 1),
        },
      }));

      notify("success", res.data?.message || t("info.logoSuccess"));
      return {
        ok: true,
        logo: nextLogo,
        logoPosition: nextLogoPosition,
      };
    } catch (err) {
      console.error("Upload logo failed:", err);
      notify("error", t("info.logoFailed"));
      return { ok: false };
    } finally {
      setSaving((p) => ({ ...p, s1: false }));
    }
  };

  useEffect(() => {
    const boot = async () => {
      try {
        setLoading(true);

        const headers = { Authorization: `Bearer ${token}` };
        const [occRes, cityRes, profRes] = await Promise.all([
          axios.get(`${config.BACKEND_BASE_URL}/api/admin/info/occasions`, { headers }),
          axios.get(`${config.BACKEND_BASE_URL}/api/admin/info/cities`, { headers }),
          axios.get(`${config.BACKEND_BASE_URL}/api/admin/info/`, { headers }),
        ]);

        const occs = ensureArray(occRes.data?.data).map((o) => ({
          value: o.nameEn,
          nameEn: o.nameEn,
          nameGr: o.nameGr || "",
        }));
        setOccasionOptions(occs);

        const cities = ensureArray(cityRes.data?.data).map((c) => ({
          value: c.nameEn,
          nameEn: c.nameEn,
          nameGr: c.nameGr,
          coords: ensureArray(c.coords),
        }));
        setCityOptions(cities);

        const data = profRes.data?.data || {};
        const merged = {
          ...emptyForm,
          ...data,
          occasions: ensureArray(data.occasions).map((o) => {
            if (typeof o === "string") {
              const found = occs.find((opt) => opt.nameEn === o);
              return { en: o, gr: found?.nameGr || "" };
            }
            return { en: String(o?.en || ""), gr: String(o?.gr || "") };
          }).filter((o) => o.en),
          covers: ensureArray(data.covers),
          location: {
            town: data.location?.town || "",
            coords: ensureArray(data.location?.coords).length === 2 ? data.location.coords : ["", ""],
            showLocation: data.location?.showLocation ?? false,
            ...parseRealAddress(data.location?.realAddress),
          },
          contact_info: { ...emptyForm.contact_info, ...(data.contact_info || {}) },
          logoPosition: { ...emptyForm.logoPosition, ...(data.logoPosition || {}) },
          price: {
            hour: normalizePrice(data.price?.hour),
            day: normalizePrice(data.price?.day),
            event: normalizePrice(data.price?.event),
          },
          googleReviews: {
            rating: data.googleReviews?.rating ?? "",
            link: data.googleReviews?.link ?? "",
          },
          includes: ensureArray(data.includes),
          coversEverywhere: data.coversEverywhere ?? false,
        };

        setForm(merged);

        initialS1.current = {
          logo: merged.logo,
          coverImg: merged.coverImg,
          logoPosition: merged.logoPosition,
        };

        initialS2.current = {
          name: merged.name,
          category: merged.category,
          parentCategory: merged.parentCategory,
          occasions: merged.occasions,
          info: merged.info,
          contactTimes: merged.contactTimes,
          contact_info: merged.contact_info,
        };

        initialS3.current = { price: merged.price, notifyBefore: merged.notifyBefore };
        initialS4.current = { location: merged.location, covers: merged.covers, coversEverywhere: merged.coversEverywhere };
        initialS5.current = { googleReviews: merged.googleReviews };
        initialS6.current = { includes: merged.includes };
      } catch (err) {
        console.error(err);
        setToast({ open: true, type: "error", msg: "Failed to load profile/options." });
      } finally {
        setLoading(false);
      }
    };

    boot();
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: "grid", placeItems: "center", minHeight: "65vh" }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
      <Stack sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={900}>
          {t("info.title")}
        </Typography>
        <Typography variant="body2" sx={{ color: "#fff" }}>
          {t("info.subtitle")}
        </Typography>
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <SectionImages
            form={form}
            setField={setField}
            updatePartial={updatePartial}
            saving={saving.s1}
            dirty={isDirty(initialS1, {
              logo: form.logo,
              coverImg: form.coverImg,
              logoPosition: form.logoPosition,
            })}
            onSaved={() => {
              initialS1.current = {
                logo: form.logo,
                coverImg: form.coverImg,
                logoPosition: form.logoPosition,
              };
            }}
            backendBase={config.BACKEND_BASE_URL}
            notify={notify}
          />
        </Grid>

        <Grid item xs={12} md={7}>
          <SectionMainInfo
            form={form}
            setField={setField}
            setNested={setNested}
            occasionOptions={occasionOptions}
            setOccasionOptions={setOccasionOptions}
            notify={notify}
            dirty={isDirty(initialS2, {
              name: form.name,
              category: form.category,
              parentCategory: form.parentCategory,
              occasions: form.occasions,
              info: form.info,
              contactTimes: form.contactTimes,
              contact_info: form.contact_info,
            })}
            onSaved={() => {
              initialS2.current = {
                name: form.name,
                category: form.category,
                parentCategory: form.parentCategory,
                occasions: form.occasions,
                info: form.info,
                contactTimes: form.contactTimes,
                contact_info: form.contact_info,
              };
            }}
          />
        </Grid>

        <Grid item xs={12} md={5}>
          <Stack spacing={2}>
            <SectionPricing
              form={form}
              setField={setField}
              setNested={setNested}
              updatePricingSection={updatePricingSection}
              saving={saving.s3}
              dirty={isDirty(initialS3, { price: form.price, notifyBefore: form.notifyBefore })}
              onSaved={() => (initialS3.current = { price: form.price, notifyBefore: form.notifyBefore })}
            />
            <SectionReviews
              form={form}
              setNested={setNested}
              updateReviewsSection={updateReviewsSection}
              saving={saving.s5}
              dirty={isDirty(initialS5, { googleReviews: form.googleReviews })}
              onSaved={() => (initialS5.current = { googleReviews: form.googleReviews })}
            />
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <SectionIncludes
            form={form}
            setField={setField}
            notify={notify}
          />
        </Grid>

        <Grid item xs={12}>
          <SectionLocation
            form={form}
            setField={setField}
            setLocation={setLocation}
            cityOptions={citySelectOptions}
            updateLocationSection={updateLocationSection}
            saving={saving.s4}
            dirty={isDirty(initialS4, { location: form.location, covers: form.covers, coversEverywhere: form.coversEverywhere })}
            onSaved={() => (initialS4.current = { location: form.location, covers: form.covers, coversEverywhere: form.coversEverywhere })}
          />
        </Grid>
      </Grid>

      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      >
        <Alert onClose={() => setToast((p) => ({ ...p, open: false }))} severity={toast.type} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
    </DashboardLayout>
  );
}